import { getRuntimeConfig } from "@/app/config/runtime-config";
import type { components } from "@/types/generated/invitation-api";

type Schemas = components["schemas"];

export type CredentialRecoveryStartRequest = Schemas["CredentialRecoveryStartRequest"];
export type CredentialRecoveryStartResult = Schemas["CredentialRecoveryStartResponse"];
export type CredentialRecoveryFinalizeRequest = Schemas["CredentialRecoveryFinalizeRequest"];
export type CredentialRecoveryFinalizeResult = Schemas["CredentialRecoveryFinalizeResponse"];
export type ContractErrorCategory = Schemas["ContractError"]["category"];

/** 011-15 UI contract: `^[A-Za-z0-9_-]{43}$`. */
export const CREDENTIAL_RECOVERY_REFERENCE_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export function isCredentialRecoveryReference(value: string): boolean {
  return CREDENTIAL_RECOVERY_REFERENCE_PATTERN.test(value);
}

/**
 * Non-2xx credential-recovery response. `category` mirrors the ContractError
 * body when the server supplied one; the caller branches on `status` and
 * `category` only — the message is generic and never carries the proof or the
 * recovery reference.
 */
export class CredentialRecoveryApiError extends Error {
  readonly status: number;
  readonly category: ContractErrorCategory | null;
  /** Raw `Retry-After` header on a 503, when the server sent one. */
  readonly retryAfter: string | null;

  constructor(
    message: string,
    status: number,
    category: ContractErrorCategory | null = null,
    retryAfter: string | null = null,
  ) {
    super(message);
    this.name = "CredentialRecoveryApiError";
    this.status = status;
    this.category = category;
    this.retryAfter = retryAfter;
  }
}

export interface CredentialRecoveryFinalizeOptions {
  /**
   * Disposal signal from the owning surface (A §8.1 "clear proof, reference,
   * and token-derived state on any terminal transition or unmount"). Aborting
   * cancels the in-flight request AND any pending `Retry-After` re-send, so
   * the serialized proof and bearer never outlive the page that dispatched them.
   */
  signal?: AbortSignal;
}

export interface CredentialRecoveryClient {
  /** `POST /registration/credential-recovery/start` — public, proof-bound, non-minting. */
  start(request: CredentialRecoveryStartRequest): Promise<CredentialRecoveryStartResult>;
  /** `POST /registration/credential-recovery/finalize` — requires the platform bearer. */
  finalize(
    request: CredentialRecoveryFinalizeRequest,
    accessToken: string,
    options?: CredentialRecoveryFinalizeOptions,
  ): Promise<CredentialRecoveryFinalizeResult>;
}

export class HttpCredentialRecoveryClient implements CredentialRecoveryClient {
  private readonly fetcher: typeof fetch;

  constructor(fetcher: typeof fetch = globalThis.fetch) {
    this.fetcher = fetcher === globalThis.fetch ? fetcher.bind(globalThis) : fetcher;
  }

  async start(request: CredentialRecoveryStartRequest): Promise<CredentialRecoveryStartResult> {
    return this.request(
      "/registration/credential-recovery/start",
      {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      },
      isStartResult,
    );
  }

  async finalize(
    request: CredentialRecoveryFinalizeRequest,
    accessToken: string,
    options: CredentialRecoveryFinalizeOptions = {},
  ): Promise<CredentialRecoveryFinalizeResult> {
    const { signal } = options;
    throwIfAborted(signal);
    const init: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(request),
      signal,
    };
    try {
      return await this.request(FINALIZE_PATH, init, isFinalizeResult);
    } catch (error) {
      // 011-15 UI contract L185: a 503 with `Retry-After` may re-send the SAME
      // body once after the interval. Never a second re-send, never a
      // different body, and never for any other status.
      const retryAfterMs = retryAfterDelayMs(error);
      if (retryAfterMs === null) {
        throw error;
      }
      await abortableDelay(retryAfterMs, signal);
      return this.request(FINALIZE_PATH, init, isFinalizeResult);
    }
  }

  private async request<T extends object>(
    path: `/${string}`,
    init: RequestInit,
    isValid: (body: object) => body is T,
  ): Promise<T> {
    const response = await this.fetcher(new URL(path, getRuntimeConfig().authBaseUrl), {
      method: "POST",
      credentials: "include",
      ...init,
    });
    const body: unknown = await response.json().catch(() => undefined);

    // Untrusted JSON: a success body must carry the FULL contract shape for
    // its `outcome` (own properties only — an inherited `outcome` from
    // prototype pollution / `__proto__` payloads must not pass). Anything
    // else fails closed as an API error so the caller's generic unavailable
    // state renders instead of a dereference throwing mid-render.
    if (response.ok && body && typeof body === "object" && isValid(body)) {
      return body;
    }

    const category =
      body &&
      typeof body === "object" &&
      typeof (body as { category?: unknown }).category === "string"
        ? ((body as { category: string }).category as ContractErrorCategory)
        : null;
    throw new CredentialRecoveryApiError(
      `Credential recovery request failed with status ${response.status}.`,
      response.status,
      category,
      response.status === 503 ? response.headers.get("Retry-After") : null,
    );
  }
}

const FINALIZE_PATH = "/registration/credential-recovery/finalize";

/** Own-property read on an untrusted object; `undefined` when absent or inherited. */
function own(value: object, key: string): unknown {
  return Object.hasOwn(value, key) ? (value as Record<string, unknown>)[key] : undefined;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/** `CredentialRecoveryStartResponse`: `started` carries a reference + expiry; `not_available` carries nulls. */
function isStartResult(body: object): body is CredentialRecoveryStartResult {
  const outcome = own(body, "outcome");
  const reference = own(body, "recoveryReference");
  const expiresAt = own(body, "expiresAt");
  if (outcome === "started") {
    return (
      isNonEmptyString(reference) &&
      isCredentialRecoveryReference(reference) &&
      isNonEmptyString(expiresAt)
    );
  }
  return outcome === "not_available" && reference === null && expiresAt === null;
}

/**
 * `CredentialRecoveryFinalizeResponse`: `completed` MUST carry a full
 * `MemberCompletionSucceeded` (the page dereferences `completion.financialHandoff`
 * and the correlation ids); `not_available` carries `completion: null`.
 */
function isFinalizeResult(body: object): body is CredentialRecoveryFinalizeResult {
  const outcome = own(body, "outcome");
  const completion = own(body, "completion");
  if (outcome === "not_available") {
    return completion === null;
  }
  if (outcome !== "completed" || !completion || typeof completion !== "object") {
    return false;
  }
  const handoff = own(completion, "financialHandoff");
  return (
    own(completion, "outcome") === "completed" &&
    own(completion, "registrationVariant") === "member" &&
    isNonEmptyString(own(completion, "completedAt")) &&
    own(completion, "nextAction") === "continue_financial_setup" &&
    isNonEmptyString(own(completion, "correlationId")) &&
    !!handoff &&
    typeof handoff === "object" &&
    own(handoff, "status") === "pending_owner_processing" &&
    isNonEmptyString(own(handoff, "correlationId"))
  );
}

/**
 * `Retry-After` window the browser will honour before surfacing the 503.
 * Below the minimum (a malformed `0`) would be a zero-delay resend of a
 * proof-bound finalize; above the maximum the wait is surfaced instead.
 * The MAX is shared with the registration-invitation client so no surface
 * ever arms a raw `setTimeout` from an unbounded header (a value above
 * 2147483s overflows the signed-32-bit timer and fires immediately).
 */
const MIN_RETRY_AFTER_SECONDS = 1;
export const MAX_RETRY_AFTER_SECONDS = 30;

function abortError(): Error {
  return new DOMException("Credential recovery finalize was aborted.", "AbortError");
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw abortError();
  }
}

/** `setTimeout` that settles early (rejecting with an AbortError) when `signal` fires. */
function abortableDelay(ms: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Delay for the single L185 re-send, or `null` (fail closed: no scheduled
 * re-send) unless the header is a finite decimal integer within
 * [MIN_RETRY_AFTER_SECONDS, MAX_RETRY_AFTER_SECONDS]. `0`, negative,
 * fractional, non-numeric, and HTTP-date forms all surface the 503 instead
 * (Copilot 3984492004).
 */
function retryAfterDelayMs(error: unknown): number | null {
  if (!(error instanceof CredentialRecoveryApiError) || error.status !== 503) {
    return null;
  }
  const seconds = parseRetryAfterSeconds(error.retryAfter);
  return seconds !== null && seconds <= MAX_RETRY_AFTER_SECONDS ? seconds * 1000 : null;
}

/**
 * Strict delta-seconds parse of a raw `Retry-After` header: only an
 * unsigned decimal integer >= MIN_RETRY_AFTER_SECONDS is usable; anything
 * else (absent, `0`, `-1`, `1.5`, `1e3`, `5abc`, HTTP-date) is `null`.
 */
export function parseRetryAfterSeconds(header: string | null | undefined): number | null {
  const trimmed = header?.trim() ?? "";
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const seconds = Number(trimmed);
  return Number.isSafeInteger(seconds) && seconds >= MIN_RETRY_AFTER_SECONDS ? seconds : null;
}
